App = App || {};
App.GeoSapienWidget = (function ($) {
    'use strict';

    var INACTIVITY_TIMEOUT_MS = 30000;
    var DEFAULT_FEEDBACK_AUTO_SUBMIT_MS = 5000;
    // Matches $bp_desktop in _client.settings.breakpoints.scss. Desktop opens the
    // widget expanded; tablet and mobile open it collapsed (per GSW-4797 AC).
    var DESKTOP_MIN_WIDTH = '(min-width: 1200px)';
    // How close to the bottom (px) still counts as "following" the stream.
    var STICK_TO_BOTTOM_THRESHOLD_PX = 24;
    var STATE_UNAVAILABLE = 'unavailable';
    var STATE_NO_CONTENT = 'no-content';
    var HTTP_UNAUTHORIZED = 401;
    var HTTP_NOT_FOUND = 404;

    var _$widget = null;
    var _$body = null;
    var _$questions = null;
    var _$selectedQuestion = null;
    var _$selectedQuestionText = null;
    var _$response = null;
    var _$responseText = null;
    var _$retry = null;
    var _$toggleIcon = null;

    var _token = null;
    var _baseUrl = null;
    var _doi = null;
    var _publisherId = null;
    var _bookChapterId = null;
    var _lastQuestionId = null;
    var _carouselIndex = 0;
    var _carouselQuestionCount = 0;
    var _sanitizerConfigured = false;
    // Streamed answers auto-scroll to the bottom as they grow. The follow pauses if
    // the user scrolls up (e.g. to read the takeaways) and resumes at the bottom.
    var _autoFollow = false;
    var _lastProgrammaticScrollTop = 0;
    // Feedback (GSW-4799): reason options come from the widget-data payload; a thumb
    // click starts a server-configured auto-submit timer that posts the default reason.
    var _feedbackOptions = [];
    var _feedbackAutoSubmitMs = DEFAULT_FEEDBACK_AUTO_SUBMIT_MS;
    var _feedbackTimer = null;
    var _feedbackSubmitted = false;

    return {
        init: init
    };

    function init() {
        _$widget = $('.js-geosapien-widget');
        if (!_$widget.length) {
            return;
        }

        _$body = _$widget.find('.js-geosapien-body');
        _$questions = _$widget.find('.js-geosapien-questions');
        _$selectedQuestion = _$widget.find('.js-geosapien-selected-question');
        _$selectedQuestionText = _$widget.find('.js-geosapien-selected-question-text');
        _$response = _$widget.find('.js-geosapien-response');
        _$responseText = _$widget.find('.js-geosapien-response-text');
        _$retry = _$widget.find('.js-geosapien-retry');
        _$toggleIcon = _$widget.find('.js-geosapien-toggle-icon');

        _doi = _extractDoi();
        _publisherId = _extractPublisherId();
        _bookChapterId = _extractBookChapterId();
        if (!_doi && !_publisherId && !_bookChapterId) {
            return;
        }

        _feedbackAutoSubmitMs = _resolveFeedbackTimeout();

        _setCtaIdentifier();
        _wireEvents();

        if ('IntersectionObserver' in window) {
            var observer = new IntersectionObserver(function (entries) {
                entries.forEach(function (entry) {
                    if (entry.isIntersecting) {
                        observer.unobserve(entry.target);
                        _initWidget();
                    }
                });
            }, { rootMargin: '200px' });
            observer.observe(_$widget[0]);
        } else {
            _initWidget();
        }
    }

    function _wireEvents() {
        _$widget.on('click', '.js-geosapien-toggle', function () {
            _setCollapsed(!_$widget.hasClass('geosapien-widget--collapsed'));
        });

        _$widget.on('click', '.js-geosapien-question', function () {
            var $pill = $(this);
            _askQuestion($pill.data('questionId'), $pill.text());
        });

        _$widget.on('click', '.js-geosapien-retry', function () {
            if (_lastQuestionId) {
                _requestAnswer();
            }
        });

        _$widget.on('click', '.js-geosapien-feedback-thumb', function () {
            _onThumbClick($(this), $(this).data('mood'));
        });

        _$widget.on('change', '.js-geosapien-feedback-select', function () {
            _completeFeedback(parseInt($(this).val(), 10));
        });

        _$widget.on('click', '.js-geosapien-cta', function () {
            _trackCtaClick();
        });

        _$widget.on('click', '.js-geosapien-prev', function () {
            _moveCarousel(-1);
        });

        _$widget.on('click', '.js-geosapien-next', function () {
            _moveCarousel(1);
        });

        // scroll does not bubble, so bind directly on the body.
        _$body.on('scroll', _onBodyScroll);
    }

    function _onBodyScroll() {
        // Ignore the scroll our own code triggers; only react to the user moving the
        // body. When the user scrolls up, stop following; resuming at the bottom
        // re-enables it.
        if (Math.abs(_$body[0].scrollTop - _lastProgrammaticScrollTop) < 2) {
            return;
        }
        _autoFollow = _isBodyNearBottom();
    }

    function _isBodyNearBottom() {
        var el = _$body[0];
        var distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
        return distanceFromBottom <= STICK_TO_BOTTOM_THRESHOLD_PX;
    }

    function _scrollBody(scrollTop) {
        _$body[0].scrollTop = scrollTop;
        // Read back the clamped value so the scroll handler recognises this as ours.
        _lastProgrammaticScrollTop = _$body[0].scrollTop;
    }

    function _followStreamIfNeeded() {
        if (_autoFollow) {
            _scrollBody(_$body[0].scrollHeight);
        }
    }

    // Desktop opens expanded with the arrow-up icon; tablet and mobile open
    // collapsed with the arrow-down icon. The toggle behaves identically at any
    // viewport once the user interacts with it.
    function _applyInitialCollapseState() {
        var isDesktop = window.matchMedia(DESKTOP_MIN_WIDTH).matches;
        _setCollapsed(!isDesktop);
    }

    function _setCollapsed(collapsed) {
        _$widget.toggleClass('geosapien-widget--collapsed', collapsed);
        _$toggleIcon
            .toggleClass('icon-general_arrow-up', !collapsed)
            .toggleClass('icon-general_arrow-down', collapsed);
        _$widget.find('.js-geosapien-toggle')
            .attr('aria-expanded', collapsed ? 'false' : 'true')
            .attr('aria-label', collapsed ? 'Expand widget' : 'Collapse widget');
    }

    function _moveCarousel(direction) {
        var next = _carouselIndex + direction;
        if (next < 0 || next >= _carouselQuestionCount) {
            return;
        }
        var $questions = _$widget.find('.js-geosapien-track .js-geosapien-question');
        $questions.eq(_carouselIndex).addClass('hidden');
        _carouselIndex = next;
        $questions.eq(_carouselIndex).removeClass('hidden');
        _updateCarouselNav();
    }

    // Navigation is bounded, not looping: the back control is disabled at the first
    // question and the forward control is disabled at the last (per GSW-4797 AC).
    function _updateCarouselNav() {
        _$widget.find('.js-geosapien-prev').prop('disabled', _carouselIndex === 0);
        _$widget.find('.js-geosapien-next').prop('disabled', _carouselIndex >= _carouselQuestionCount - 1);
    }

    function _extractDoi() {
        var path = window.location.pathname;
        var doiMatch = path.match(/\/doi\/(10\.\d{4,}\/[^\/]+)/);
        if (doiMatch) {
            return doiMatch[1];
        }
        var $metaDoi = $('meta[name="citation_doi"]');
        if ($metaDoi.length) {
            return $metaDoi.attr('content');
        }
        var $doiEl = $('[data-doi]').first();
        if ($doiEl.length) {
            return $doiEl.data('doi');
        }
        return null;
    }

    // data-publisher-id is server-emitted for articles only (Article.LegacyArticleID);
    // chapters do not have a Publisher ID in GSW's content model.
    function _extractPublisherId() {
        return _$widget.attr('data-publisher-id') || null;
    }

    // data-book-chapter-id is server-emitted for chapters that have no DOI of their own:
    // the combined [book-DOI]_[legacySectionId] fallback identifier (GSW-4820). Its
    // presence means the server has decided this page uses the X-BOOK-CHAPTER-ID path.
    function _extractBookChapterId() {
        return _$widget.attr('data-book-chapter-id') || null;
    }

    // The auto-submit window (seconds) is server-configured via
    // GeoSapien.FeedbackAutoSubmitSeconds and emitted as data-feedback-timeout-seconds.
    // Fall back to the default if it is missing or not a positive number.
    function _resolveFeedbackTimeout() {
        var seconds = parseInt(_$widget.attr('data-feedback-timeout-seconds'), 10);
        if (isNaN(seconds) || seconds <= 0) {
            return DEFAULT_FEEDBACK_AUTO_SUBMIT_MS;
        }
        return seconds * 1000;
    }

    // Identifier resolution order (GSW-4820):
    //   1. A chapter with no DOI of its own carries a server-emitted book-chapter-id
    //      (book DOI + legacySectionId) — sent alone as X-BOOK-CHAPTER-ID; X-DOI and
    //      X-PUBLISHER-ID are not populated in this case.
    //   2. Otherwise the existing paths are unchanged: X-DOI for an article / chapter
    //      DOI and/or X-PUBLISHER-ID for an article Publisher ID.
    function _buildIdentifierParams() {
        if (_bookChapterId) {
            return 'X-BOOK-CHAPTER-ID=' + encodeURIComponent(_bookChapterId);
        }
        var params = [];
        if (_doi) {
            params.push('X-DOI=' + encodeURIComponent(_doi));
        }
        if (_publisherId) {
            params.push('X-PUBLISHER-ID=' + encodeURIComponent(_publisherId));
        }
        return params.join('&');
    }

    // The CTA opens the standalone GeoSapienAI app; append the same identifier the widget
    // uses for its Fabriq calls so GeoSapienAI knows which content the user came from.
    function _setCtaIdentifier() {
        var params = _buildIdentifierParams();
        if (!params) {
            return;
        }
        var $cta = _$widget.find('.js-geosapien-cta');
        var baseHref = $cta.attr('href') || '';
        var separator = baseHref.indexOf('?') >= 0 ? '&' : '?';
        $cta.attr('href', baseHref + separator + params);
    }
    function _initWidget() {
        _getToken().then(function (tokenData) {
            if (!tokenData) {
                _setState(STATE_UNAVAILABLE);
                return;
            }

            _token = tokenData.token;
            _baseUrl = tokenData.baseUrl;

            _fetchWidgetData().then(function (outcome) {
                if (!outcome.isSuccess) {
                    _setState(STATE_UNAVAILABLE);
                    return;
                }

                var data = outcome.data;
                _feedbackOptions = data.question_feedback_options || [];
                // Fabriq is correcting the widget-data field `takeways` -> `takeaways`.
                // Read both during the transition; drop `takeways` once the corrected
                // payload is live on STG.
                var takeaways = data.takeaways || data.takeways || [];
                var questions = data.questions || [];
                if (!takeaways.length && !questions.length) {
                    _setState(STATE_NO_CONTENT);
                    return;
                }

                _renderTakeaways(takeaways);
                _renderQuestions(questions);
                _$widget.removeAttr('data-state');
                _applyInitialCollapseState();
            });
        });
    }
    function _setState(state) {
        _$widget.attr('data-state', state);
    }

    function _getToken() {
        var cached = sessionStorage.getItem('geosapien_token');
        if (cached) {
            try {
                return $.Deferred().resolve(JSON.parse(cached)).promise();
            } catch (e) {
                sessionStorage.removeItem('geosapien_token');
            }
        }

        return $.Deferred(function (deferred) {
            SCM.Ajax.get('/api/geosapien/token')
                .expectJson()
                .then(function (data) {
                    sessionStorage.setItem('geosapien_token', JSON.stringify(data));
                    deferred.resolve(data);
                })
                .catch(function () {
                    deferred.resolve(null);
                });
        }).promise();
    }

    function _refreshTokenAndRetry(fetchFn) {
        sessionStorage.removeItem('geosapien_token');
        return _getToken().then(function (tokenData) {
            if (!tokenData) {
                return null;
            }
            _token = tokenData.token;
            return fetchFn();
        });
    }

    // NOTE: js.md prefers SCM.Ajax for HTTP, but the Fabriq calls are cross-origin
    // (different host from the SCM site) and one of them — the SSE stream — needs
    // a ReadableStream from the response body. Native fetch is the only API in the
    // platform that supports both, so this module deliberately uses fetch. SCM.Ajax
    // is still used for the same-origin server-side token call (see _getToken).
    function _fabriqFetch(path, options) {
        var url = _baseUrl + path;

        function buildOpts() {
            return $.extend({}, options, {
                headers: $.extend({ 'Authorization': 'Bearer ' + _token }, (options && options.headers) || {})
            });
        }

        return fetch(url, buildOpts()).then(function (r) {
            if (r.status === HTTP_UNAUTHORIZED) {
                return _refreshTokenAndRetry(function () {
                    return fetch(url, buildOpts());
                });
            }
            return r;
        });
    }
    function _fetchWidgetData() {
        return _fabriqFetch('/api/widget/data/?' + _buildIdentifierParams())
            .then(function (r) {
                if (!r) {
                    return { isSuccess: false };
                }
                if (r.status === HTTP_NOT_FOUND) {
                    return { isSuccess: true, data: {} };
                }
                if (!r.ok) {
                    return { isSuccess: false };
                }
                return r.json().then(function (data) {
                    return data ? { isSuccess: true, data: data } : { isSuccess: false };
                });
            })
            .catch(function () {
                return { isSuccess: false };
            });
    }

    function _renderTakeaways(takeaways) {
        var $container = _$widget.find('.js-geosapien-takeaways').empty();
        takeaways.forEach(function (t) {
            $('<li>')
                .addClass('geosapien-takeaway')
                .text(t.text)
                .appendTo($container);
        });
    }

    function _renderQuestions(questions) {
        var $track = _$widget.find('.js-geosapien-track').empty();

        // Build pills with jQuery so attribute and text values are encoded by the DOM,
        // not interpolated into an HTML string. Anything from Fabriq (q.id, q.text) is
        // therefore safe even if it contains quotes or angle brackets. Order matches
        // the order Fabriq returns in the payload.
        questions.forEach(function (q) {
            $('<button>')
                .attr('type', 'button')
                .addClass('geosapien-question js-geosapien-question')
                .attr('data-question-id', q.id)
                .text(q.text)
                .appendTo($track);
        });

        _carouselQuestionCount = questions.length;
        _carouselIndex = 0;

        // One question visible at a time; the rest are hidden and reached via the nav.
        var $questions = $track.find('.js-geosapien-question');
        $questions.addClass('hidden').eq(0).removeClass('hidden');
        _updateCarouselNav();
    }

    function _askQuestion(questionId, questionText) {
        _lastQuestionId = questionId;
        _enterSecondaryState(questionText);
        _requestAnswer();
    }

    // Transition from the initial (takeaways + questions) layout to the secondary
    // layout: hide the carousel, show the selected question right-aligned at the top
    // of the body with the response streaming underneath. The feedback controls stay
    // hidden until the answer completes (see _showFeedback). Takeaways stay above the
    // question and come back into view on scroll up.
    function _enterSecondaryState(questionText) {
        // Pin the body to its current (initial-state) height — both min and max — so
        // the widget's overall on-page height stays the same in the secondary state
        // (AC 3); the body then scrolls internally. Measure before mutating.
        var initialBodyHeight = _$body.outerHeight();
        _$body
            .css({ 'min-height': initialBodyHeight + 'px', 'max-height': initialBodyHeight + 'px' })
            .addClass('geosapien-body--secondary');

        _$questions.addClass('hidden');
        _$selectedQuestionText.text(questionText);
        _$selectedQuestion.removeClass('hidden');
        _$response.removeClass('hidden');
        _$widget.find('.js-geosapien-feedback-controls').addClass('hidden');

        _scrollSelectedQuestionToTop();
    }

    function _scrollSelectedQuestionToTop() {
        var bodyEl = _$body[0];
        var questionEl = _$selectedQuestion[0];
        _scrollBody(bodyEl.scrollTop + questionEl.getBoundingClientRect().top - bodyEl.getBoundingClientRect().top);
    }

    function _requestAnswer() {
        _$responseText.html('<span class="geosapien-spinner"></span> Thinking...');
        // Hold screen-reader announcement until the streamed answer is complete.
        _$responseText.attr('aria-busy', 'true');
        _$retry.addClass('hidden');

        _fabriqFetch('/api/widget/chat/?' + _buildIdentifierParams(), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ question_id: _lastQuestionId })
        })
        .then(function (r) {
            if (!r || !r.ok) {
                _showRetry();
                return;
            }
            return r.json();
        })
        .then(function (data) {
            if (!data || !data.execution_id) {
                _showRetry();
                return;
            }
            _connectStream(data.execution_id);
        })
        .catch(function () {
            _showRetry();
        });
    }

    function _connectStream(executionId) {
        var streamPath = '/api/widget/execution/' + executionId + '/?' + _buildIdentifierParams();
        var state = {
            buffer: '',
            partial: '',
            timeout: null
        };

        _$responseText.empty();
        // Follow the answer to the bottom as it streams in.
        _autoFollow = true;
        _resetStreamTimeout(state);

        // Route through _fabriqFetch so a 401 mid-session triggers a token refresh
        // and a single retry — same behaviour as the non-streaming Fabriq calls.
        _fabriqFetch(streamPath)
            .then(function (response) { _onStreamResponse(response, state); })
            .catch(function () { _abortStreamWithRetry(state); });
    }

    function _resetStreamTimeout(state) {
        if (state.timeout) {
            clearTimeout(state.timeout);
        }
        state.timeout = setTimeout(_showRetry, INACTIVITY_TIMEOUT_MS);
    }

    function _abortStreamWithRetry(state) {
        clearTimeout(state.timeout);
        _showRetry();
    }

    function _onStreamResponse(response, state) {
        if (!response.ok) {
            _abortStreamWithRetry(state);
            return;
        }
        var reader = response.body.getReader();
        var decoder = new TextDecoder();
        _readStreamChunk(reader, decoder, state);
    }

    function _readStreamChunk(reader, decoder, state) {
        reader.read()
            .then(function (result) { _onStreamChunk(result, reader, decoder, state); })
            .catch(function () { _abortStreamWithRetry(state); });
    }

    function _onStreamChunk(result, reader, decoder, state) {
        if (result.done) {
            clearTimeout(state.timeout);
            _$responseText.attr('aria-busy', 'false');
            _showFeedback();
            return;
        }

        _resetStreamTimeout(state);
        state.partial += decoder.decode(result.value, { stream: true });
        var lines = state.partial.split('\n');
        state.partial = lines.pop();

        for (var i = 0; i < lines.length; i++) {
            _processStreamLine(lines[i], state);
        }

        _readStreamChunk(reader, decoder, state);
    }

    function _processStreamLine(line, state) {
        if (!line.startsWith('data: ')) {
            return;
        }
        var eventData = _tryParseEvent(line.substring(6));
        if (eventData === null) {
            return;
        }
        if (eventData.name === 'answer' && eventData.delta && eventData.delta.text) {
            state.buffer += eventData.delta.text;
            _renderStreamBuffer(state.buffer);
        }
        if (eventData.name === 'termination') {
            clearTimeout(state.timeout);
            _$responseText.attr('aria-busy', 'false');
            _showFeedback();
        }
    }

    function _tryParseEvent(payload) {
        try {
            return JSON.parse(payload);
        } catch (parseError) {
            // Malformed SSE events are dropped — the stream recovers on the next event.
            return null;
        }
    }

    function _renderStreamBuffer(buffer) {
        // marked + DOMPurify are deferred CDN loads — fall back to escaped text if
        // either isn't ready yet (shouldn't happen by the time the user has clicked
        // a question, but defensive).
        if (typeof DOMPurify !== 'undefined' && typeof marked !== 'undefined') {
            _configureSanitizer();
            _$responseText.html(DOMPurify.sanitize(marked.parse(buffer)));
            _renderCitationIcons();
        } else {
            _$responseText.text(buffer);
        }
        _followStreamIfNeeded();
    }

    // Fabriq marks each citation as a link with the visible text "source" (GSW-4797
    // Technical Notes scenario 14); article-specific answers carry no such links. Swap
    // those "source" links for the citation icon glyph, keeping the href + new-tab, and
    // leave any other link untouched. Re-run on every chunk because the buffer is
    // re-rendered from scratch as it streams.
    function _renderCitationIcons() {
        _$responseText.find('a').each(function () {
            var $link = $(this);
            if ($.trim($link.text()).toLowerCase() !== 'source') {
                return;
            }
            $link
                .addClass('geosapien-citation')
                .attr('aria-label', 'Open cited source')
                .html('<i class="geosapien-citation__icon icon-file-text" aria-hidden="true"></i>');
        });
    }

    // Citations stream in as markdown links; force them to open in a new tab so the
    // reader keeps their place on the article page. DOMPurify strips target/rel by
    // default, so re-add them after sanitisation.
    function _configureSanitizer() {
        if (_sanitizerConfigured || typeof DOMPurify === 'undefined') {
            return;
        }
        DOMPurify.addHook('afterSanitizeAttributes', function (node) {
            if (node.tagName === 'A') {
                node.setAttribute('target', '_blank');
                node.setAttribute('rel', 'noopener noreferrer');
            }
        });
        _sanitizerConfigured = true;
    }

    // ----
    // Feedback (GSW-4799) — markup, classes and behaviour preserved from master.
    // ----

    // Reveals the thumbs-up / thumbs-down controls once a streamed answer completes,
    // resetting any prior feedback interaction (the same widget can answer more than
    // one question before the page reloads).
    function _showFeedback() {
        _clearFeedbackTimer();
        _feedbackSubmitted = false;

        _$widget.find('.js-geosapien-feedback-thumb').each(function () {
            $(this).removeClass('geosapien-feedback-thumb--selected');
            _setThumbGlyph($(this), false);
        });
        _$widget.find('.js-geosapien-feedback-reasons').empty().addClass('hidden');
        _$widget.find('.js-geosapien-feedback-thanks').addClass('hidden');
        // Reveal the thumbs/controls now that the answer has completed; clear any prior
        // submitted state so a second answer can be rated.
        _$widget.find('.js-geosapien-feedback-controls')
            .removeClass('geosapien-feedback-controls--submitted')
            .removeClass('hidden');
    }

    // On a thumb click: highlight the clicked thumb (swapping its glyph to the filled
    // variant), render the mood-filtered reason dropdown (default reason on top), and
    // start the auto-submit timer. Once feedback has been submitted the controls are
    // locked, so further clicks are ignored.
    function _onThumbClick($thumb, mood) {
        if (_feedbackSubmitted) {
            return;
        }

        _$widget.find('.js-geosapien-feedback-thumb').each(function () {
            $(this).removeClass('geosapien-feedback-thumb--selected');
            _setThumbGlyph($(this), false);
        });
        $thumb.addClass('geosapien-feedback-thumb--selected');
        _setThumbGlyph($thumb, true);

        var moodOptions = _feedbackOptions.filter(function (opt) {
            return opt.mood === mood;
        });
        var defaultOption = _findDefaultOption(moodOptions);

        _renderReasons(moodOptions, defaultOption);
        _startFeedbackTimer(defaultOption);
    }

    // Swaps a thumb's icon-font glyph between its outline and filled variant.
    function _setThumbGlyph($thumb, filled) {
        var base = $thumb.data('mood') === 'positive' ? 'icon-geosapien_thumb-up' : 'icon-geosapien_thumb-down';
        $thumb.find('i').attr('class', filled ? base + '-filled' : base);
    }

    function _findDefaultOption(moodOptions) {
        for (var i = 0; i < moodOptions.length; i++) {
            if (moodOptions[i].default) {
                return moodOptions[i];
            }
        }
        return null;
    }

    // Builds the reason dropdown as a native <select> so it reads as a dropdown menu
    // for keyboard and screen-reader users. The default reason is the first option;
    // because it is pre-selected, the change event only fires when the user actively
    // picks a different reason.
    function _renderReasons(moodOptions, defaultOption) {
        var $reasons = _$widget.find('.js-geosapien-feedback-reasons').empty();
        var $select = $('<select>')
            .addClass('geosapien-feedback-select js-geosapien-feedback-select')
            .attr('aria-label', 'Select a reason for your feedback');

        if (defaultOption) {
            _appendReasonOption($select, defaultOption);
        }

        // jQuery construction encodes attribute and text values; do not concatenate HTML
        // with values returned by Fabriq.
        moodOptions.forEach(function (opt) {
            if (!opt.default) {
                _appendReasonOption($select, opt);
            }
        });

        $select.appendTo($reasons);
        $reasons.removeClass('hidden');
    }

    function _appendReasonOption($select, opt) {
        $('<option>')
            .attr('value', opt.id)
            .text(opt.text)
            .appendTo($select);
    }

    // When the timer expires the default reason for the clicked mood is auto-submitted.
    // If the payload carried no default for that mood we still lock the control (showing
    // the thank-you message) without a POST, so the widget never errors.
    function _startFeedbackTimer(defaultOption) {
        _clearFeedbackTimer();
        _feedbackTimer = setTimeout(function () {
            _completeFeedback(defaultOption ? defaultOption.id : null);
        }, _feedbackAutoSubmitMs);
    }

    function _clearFeedbackTimer() {
        if (_feedbackTimer) {
            clearTimeout(_feedbackTimer);
            _feedbackTimer = null;
        }
    }

    // Single entry point for both the user-pick and timer-expiry paths. The submitted
    // flag guarantees exactly one POST per feedback interaction even if a pick and the
    // timer race, and the timer is cancelled atomically here before anything else runs.
    function _completeFeedback(optionId) {
        if (_feedbackSubmitted) {
            return;
        }
        _feedbackSubmitted = true;
        _clearFeedbackTimer();

        if (optionId) {
            _submitFeedback(_lastQuestionId, optionId);
        }

        // The reason dropdown is replaced with the thank-you message. On desktop the
        // thumbs stay visible (the picked one filled); on mobile the --submitted state
        // hides them so the thank-you message takes their place (see SCSS).
        _$widget.find('.js-geosapien-feedback-reasons').empty().addClass('hidden');
        _$widget.find('.js-geosapien-feedback-thanks').removeClass('hidden');
        _$widget.find('.js-geosapien-feedback-controls').addClass('geosapien-feedback-controls--submitted');
    }

    function _submitFeedback(questionId, optionId) {
        _fabriqFetch('/api/widget/feedback/?' + _buildIdentifierParams(), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                question: questionId,
                feedback_question_option: optionId
            })
        });
    }

    function _showRetry() {
        _$responseText.empty();
        _$responseText.attr('aria-busy', 'false');
        _$retry.removeClass('hidden');
    }

    function _trackCtaClick() {
        _fabriqFetch('/api/widget/feedback/?' + _buildIdentifierParams(), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                feedback_type: 'USER',
                feedback_user_option: 1
            })
        });
    }

})(jQuery);

$(function () {
    App.GeoSapienWidget.init();
});
